import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaArrowLeft, FaCube, FaImage, FaMicrophone, FaVideo, FaRobot } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './ModelExplorer.css';

const modelsData = [
  // TEXT
  { id: 1, name: 'Gemini 3.5 Flash', category: 'Text', rpm: '5', tpm: '250K', rpd: '20', status: 'Active' },
  { id: 2, name: 'Gemini 3 Flash', category: 'Text', rpm: '5', tpm: '250K', rpd: '20', status: 'Active' },
  { id: 3, name: 'Gemini 3.1 Flash Lite', category: 'Text', rpm: '15', tpm: '250K', rpd: '500', status: 'Active' },
  { id: 4, name: 'Gemini 2.5 Flash', category: 'Text', rpm: '5', tpm: '250K', rpd: '20', status: 'Active' },
  { id: 5, name: 'Gemini 2.5 Flash Lite', category: 'Text', rpm: '10', tpm: '250K', rpd: '20', status: 'Active' },
  { id: 6, name: 'Gemini 2.5 Pro', category: 'Text', rpm: '0', tpm: '0', rpd: '0', status: 'Not Available' },
  { id: 7, name: 'Gemini 3.1 Pro', category: 'Text', rpm: '0', tpm: '0', rpd: '0', status: 'Not Available' },
  
  // OPEN MODELS
  { id: 8, name: 'Gemma 4 26B', category: 'Text', rpm: '15', tpm: 'Unlimited', rpd: '1500', status: 'Active' },
  { id: 9, name: 'Gemma 4 31B', category: 'Text', rpm: '15', tpm: 'Unlimited', rpd: '1500', status: 'Active' },

  // IMAGE
  { id: 10, name: 'Imagen 4 Fast Generate', category: 'Image', dailyGen: '25', status: 'Active' },
  { id: 11, name: 'Imagen 4 Generate', category: 'Image', dailyGen: '25', status: 'Active' },
  { id: 12, name: 'Imagen 4 Ultra Generate', category: 'Image', dailyGen: '25', status: 'Active' },
  { id: 13, name: 'Nano Banana', category: 'Image', status: 'Restricted' },
  { id: 14, name: 'Nano Banana Pro', category: 'Image', status: 'Restricted' },
  { id: 15, name: 'Nano Banana 2', category: 'Image', status: 'Restricted' },

  // VIDEO
  { id: 16, name: 'Veo 3 Fast Generate', category: 'Video', status: 'Restricted' },
  { id: 17, name: 'Veo 3 Generate', category: 'Video', status: 'Restricted' },
  { id: 18, name: 'Veo 3 Lite Generate', category: 'Video', status: 'Restricted' },

  // AUDIO & SPEECH
  { id: 19, name: 'Gemini 2.5 Flash TTS', category: 'Audio', rpm: '3', tpm: '10K', rpd: '10', status: 'Active' },
  { id: 20, name: 'Gemini 3.1 Flash TTS', category: 'Audio', rpm: '3', tpm: '10K', rpd: '10', status: 'Active' },
  { id: 21, name: 'Gemini 2.5 Flash Native Audio Dialog', category: 'Audio', rpm: 'Unlimited', tpm: '1M', status: 'Active' },
  { id: 22, name: 'Gemini 3 Flash Live', category: 'Audio', rpm: 'Unlimited', tpm: '65K', status: 'Active' },
  { id: 23, name: 'Gemini 3.5 Live Translate', category: 'Audio', rpm: 'Unlimited', tpm: '20K', status: 'Active' },

  // EMBEDDING
  { id: 24, name: 'Gemini Embedding 1', category: 'Embedding', rpm: '100', tpm: '30K', rpd: '1000', status: 'Active' },
  { id: 25, name: 'Gemini Embedding 2', category: 'Embedding', rpm: '100', tpm: '30K', rpd: '1000', status: 'Active' },

  // ROBOTICS
  { id: 26, name: 'Gemini Robotics ER 1.5 Preview', category: 'Robotics', rpm: '10', tpm: '250K', rpd: '20', status: 'Preview' },
  { id: 27, name: 'Gemini Robotics ER 1.6 Preview', category: 'Robotics', rpm: '5', tpm: '250K', rpd: '20', status: 'Preview' },

  // RESEARCH & AGENTS
  { id: 28, name: 'Deep Research Pro Preview', category: 'Research', status: 'Not Available' },
  { id: 29, name: 'Antigravity', category: 'Research', status: 'Not Available' },
  { id: 30, name: 'Computer Use Preview', category: 'Research', status: 'Experimental' },
];

const categories = ['All', 'Text', 'Image', 'Video', 'Audio', 'Embedding', 'Robotics', 'Research'];

function ModelExplorer() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredModels = useMemo(() => {
    return modelsData.filter((model) => {
      const matchesSearch = model.name.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || model.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [search, selectedCategory]);

  const getStatusClass = (status) => {
    switch(status) {
      case 'Active': return 'status-active';
      case 'Preview': return 'status-preview';
      case 'Experimental': return 'status-experimental';
      case 'Restricted': return 'status-restricted';
      case 'Not Available': return 'status-not-available';
      default: return 'status-active';
    }
  };

  return (
    <div className="explorer-container">
      <div className="back-to-home" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>
        <FaArrowLeft /> Back to Home
      </div>

      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="explorer-header"
      >
        <h1>Gemini Model Explorer</h1>
        <p>Browse all Gemini, Imagen, Veo, Gemma, Robotics, Audio and Embedding models in one place.</p>
        
        <div className="stats-ribbon">
          <div className="stat-badge"><FaCube /> Total Models: <strong>{modelsData.length}</strong></div>
          <div className="stat-badge"><FaSearch /> Text: <strong>{modelsData.filter(m => m.category === 'Text').length}</strong></div>
          <div className="stat-badge"><FaImage /> Image: <strong>{modelsData.filter(m => m.category === 'Image').length}</strong></div>
          <div className="stat-badge"><FaVideo /> Video: <strong>{modelsData.filter(m => m.category === 'Video').length}</strong></div>
          <div className="stat-badge"><FaMicrophone /> Audio: <strong>{modelsData.filter(m => m.category === 'Audio').length}</strong></div>
          <div className="stat-badge"><FaRobot /> Live APIs: <strong>{modelsData.filter(m => m.status === 'Active').length}</strong></div>
        </div>
      </motion.div>

      <div className="controls-container">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input 
            type="text" 
            placeholder="Search models..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="category-filters">
          {categories.map(cat => (
            <button 
              key={cat}
              className={`filter-btn ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <motion.div layout className="models-grid">
        <AnimatePresence>
          {filteredModels.map((model) => (
            <motion.div
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              key={model.id}
              className="model-card"
            >
              <div className="card-header">
                <div>
                  <h3 className="card-title">{model.name}</h3>
                  <div className="card-category">{model.category}</div>
                </div>
                <div className={`status-badge ${getStatusClass(model.status)}`}>
                  {model.status}
                </div>
              </div>

              <div className="quotas-grid">
                {model.rpm && (
                  <div className="quota-item">
                    <span className="quota-label">RPM (Requests)</span>
                    <span className="quota-value">{model.rpm}</span>
                  </div>
                )}
                {model.tpm && (
                  <div className="quota-item">
                    <span className="quota-label">TPM (Tokens)</span>
                    <span className="quota-value">{model.tpm}</span>
                  </div>
                )}
                {model.rpd && (
                  <div className="quota-item">
                    <span className="quota-label">RPD (Daily)</span>
                    <span className="quota-value">{model.rpd}</span>
                  </div>
                )}
                {model.dailyGen && (
                  <div className="quota-item">
                    <span className="quota-label">Daily Generations</span>
                    <span className="quota-value">{model.dailyGen}</span>
                  </div>
                )}
                {!model.rpm && !model.dailyGen && (
                  <div className="quota-item" style={{ gridColumn: 'span 2' }}>
                    <span className="quota-label">Quota Information</span>
                    <span className="quota-value">Not published</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {filteredModels.length === 0 && (
        <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: '40px' }}>
          No models found matching your criteria.
        </div>
      )}
    </div>
  );
}

export default ModelExplorer;
